import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { autoAuctionApiSdk } from '../sdk/autoAuctionClient.js';
import { idOf } from '../sdk/identity.js';
import { saleDisplayName } from '../sdk/saleDisplayName.js';

export const saleIndexStore = defineStore('saleIndexStore', () => {
    const sales = ref([]);
    const loaded = ref(false);
    const loading = ref(false);

    // THE THREE IDS A LANE CARRIES, because they answer three different
    // questions and conflating them is what the whole durable-id arc is about:
    //
    //   saleRefId  the SALE's minted id — its own sale_event document. The
    //              durable key: a follow, the attended sale, a desk mute.
    //              Shared by a sale's lanes and unchanged by a rename.
    //   laneRefId  the LANE's minted id — its own sale_lane document. The
    //              durable key for whatever is saved about one lane rather
    //              than the sale.
    //   eventId    the LANE KEY: the lane-level document's own `_id`, which
    //              is the event runtime's own event id. Derived from the names
    //              and the day, so it changes under a rename — fine for talking
    //              to the runtime, never for saving anything.

    // itemId -> the lane it runs in. A sale document is one LANE, and its
    // runList carries real catalog item `_id`s — the same ids the feed's
    // documents use — so this is a plain string keying, no translation.
    const byItemId = computed(() => {
        const map = new Map();
        for (const lane of sales.value) {
            const eventId = idOf(lane);
            if (!eventId) continue;
            const runList = Array.isArray(lane.runList) ? lane.runList : [];
            runList.forEach((itemId, index) => {
                // First lane wins. An item shouldn't be in two lanes at once —
                // the runtime rejects a duplicate runList id across events — so
                // a collision means the seed is wrong, not that we should guess.
                if (map.has(itemId)) return;
                map.set(itemId, laneEntry(lane, eventId, index));
            });
        }
        return map;
    });

    function laneEntry(lane, eventId, runNumber = null) {
        return {
            saleRefId: lane.saleRefId ?? null,
            laneRefId: lane.laneRefId ?? null,
            eventId,
            saleName: lane.saleName,
            laneName: lane.laneName ?? null,
            eventType: lane.eventType,
            location: lane.location ?? null,
            auctionHouseCode: lane.auctionHouseCode ?? null,
            // The house's own document id. The join a followed house selects
            // its sales by; the slug above is drawn and routed by.
            auctionHouseId: lane.auctionHouseId ?? null,
            startAt: lane.startAt ?? null,
            runNumber
        };
    }

    // The lane key -> the lane it is. This is the one bridge between what the
    // runtime says and what a dealer saved: a live lot carries its `eventId`,
    // and everything the app keys by sale or by lane is reached through here.
    const byEventId = computed(() => {
        const map = new Map();
        for (const lane of sales.value) {
            const eventId = idOf(lane);
            if (!eventId) continue;
            map.set(String(eventId), laneEntry(lane, eventId));
        }
        return map;
    });

    // A display name -> every sale id that answers to it. A set, because a
    // weekly name repeats: the same words name last week's sale and this
    // week's. Only ever read by the on-device migrations, which have nothing but
    // a name to go on — and a name that names two sales resolves to neither.
    const saleRefIdsByName = computed(() => {
        const map = new Map();
        for (const lane of sales.value) {
            const key = saleDisplayName(lane.saleName);
            if (!key || !lane.saleRefId) continue;
            if (!map.has(key)) map.set(key, new Set());
            map.get(key).add(lane.saleRefId);
        }
        return map;
    });

    // Load once per session. Never throws: a failed load leaves the map empty and
    // every card renders unscheduled, which is exactly the intended degradation.
    const ensureLoaded = async () => {
        if (loaded.value || loading.value) return;
        loading.value = true;
        try {
            sales.value = await autoAuctionApiSdk.getSales();
            loaded.value = true;
        } catch (err) {
            console.warn('Sale index load failed — cards will render as unscheduled', err);
        } finally {
            loading.value = false;
        }
    };

    return {
        sales,
        loaded,
        loading,
        byItemId,
        byEventId,
        saleRefIdsByName,
        ensureLoaded,
        saleFor: (itemId) =>
            (itemId ? byItemId.value.get(String(itemId)) ?? null : null),
        /** The lane a lane key names, or null while the index has not answered. */
        laneForEvent: (eventId) =>
            (eventId ? byEventId.value.get(String(eventId)) ?? null : null),
        /** The sale a runtime event belongs to, by its minted id. */
        saleRefIdForEvent: (eventId) =>
            (eventId ? byEventId.value.get(String(eventId))?.saleRefId ?? null : null),
        /** Every sale that answers to this name — none, one, or several. */
        saleRefIdsForName: (name) => [...(saleRefIdsByName.value.get(saleDisplayName(name)) ?? [])],
        /**
         * The one sale this name names, or null when it names none or more than
         * one. Ambiguity is not resolved by guessing: a migration leaves the
         * name pending and asks again later.
         */
        saleRefIdForName: (name) => {
            const found = saleRefIdsByName.value.get(saleDisplayName(name));
            return found?.size === 1 ? [...found][0] : null;
        }
    };
});

export default saleIndexStore;
